import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function AIAutomationIntegrationPage() {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    companySize: '',
    services: [] as string[],
    message: '',
    consent: false,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.consent) {
      alert('Please accept the privacy policy to continue.');
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('company', formData.company);
      formBody.append('companySize', formData.companySize);
      formBody.append('services', formData.services.join(', '));
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d50s72d1e0me1634tfi0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setShowSuccessModal(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          companySize: '',
          services: [],
          message: '',
          consent: false,
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const scrollToForm = () => {
    document.getElementById('demo-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToIntegration = () => {
    document.getElementById('integration-capabilities')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section 
        className="relative w-full py-20 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://thyamis.com/wp-content/uploads/2025/01/ai-automation-integration-hero-bg.jpg)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/70"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-2xl">
            <div className="mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-600">
                <div className="w-4 h-4 flex items-center justify-center mr-2">
                  <i className="ri-robot-line"></i>
                </div>
                AI Automation & Integration
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              AI Automation & Integration Solutions
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              Transform your business operations with cutting-edge AI automation, robotic process automation (RPA), and seamless system integrations. Eliminate manual tasks, streamline workflows, and connect your entire technology ecosystem.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={scrollToForm}
                className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium inline-flex items-center hover:bg-primary/90 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-calendar-line"></i>
                </div>
                Schedule Demo
              </button>
              <button
                onClick={scrollToIntegration}
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-button whitespace-nowrap font-medium inline-flex items-center hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-links-line"></i>
                </div>
                View Integration Solutions
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Core Automation Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Core Automation Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive AI automation solutions designed to optimize your business processes, reduce operational costs, and accelerate digital transformation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-robot-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Robotic Process Automation</h3>
              <p className="text-gray-600 mb-6">
                Automate repetitive tasks and business processes with intelligent software robots that work 24/7 with perfect accuracy.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Data entry automation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Document processing</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Report generation</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-green-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-links-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">API Integration Hub</h3>
              <p className="text-gray-600 mb-6">
                Connect disparate systems and applications through robust API integrations, enabling seamless data flow across your organization.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">RESTful API development</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Third-party integrations</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Real-time synchronization</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                  <i className="ri-flow-chart text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Workflow Automation</h3>
              <p className="text-gray-600 mb-6">
                Design and deploy intelligent workflows that automatically route tasks, trigger actions, and optimize business processes.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Visual workflow designer</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Conditional logic & triggers</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Multi-system orchestration</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-orange-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-orange-600">
                  <i className="ri-building-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Enterprise Connectivity</h3>
              <p className="text-gray-600 mb-6">
                Establish secure, scalable connections between enterprise systems, cloud platforms, and legacy applications.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-orange-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Legacy system integration</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-orange-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Cloud platform connectivity</span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-orange-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">Secure data exchange</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Capabilities */}
      <section className="py-16" id="integration-capabilities">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Integration Capabilities</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive integration solutions that connect your entire technology ecosystem, enabling seamless data flow and process automation across platforms.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-settings-3-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-6">System Integration Types</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-primary mt-0.5 mr-3">
                    <i className="ri-database-2-line"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Database Integration</h4>
                    <p className="text-sm text-gray-600">Real-time data synchronization across multiple databases</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-primary mt-0.5 mr-3">
                    <i className="ri-cloud-line"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Cloud Integration</h4>
                    <p className="text-sm text-gray-600">Seamless connectivity with AWS, Azure, and Google Cloud</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-primary mt-0.5 mr-3">
                    <i className="ri-smartphone-line"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Application Integration</h4>
                    <p className="text-sm text-gray-600">Connect CRM, ERP, and business applications</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center text-primary mt-0.5 mr-3">
                    <i className="ri-shield-check-line"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Secure Integration</h4>
                    <p className="text-sm text-gray-600">Enterprise-grade security and compliance</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <div className="w-16 h-16 bg-green-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-stack-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-6">Supported Platforms</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Business Systems</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-microsoft-fill"></i>
                      </div>
                      Microsoft 365
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-google-fill"></i>
                      </div>
                      Google Workspace
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-building-line"></i>
                      </div>
                      Salesforce
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-pie-chart-line"></i>
                      </div>
                      SAP ERP
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">E-commerce Platforms</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-shopping-bag-line"></i>
                      </div>
                      Shopify
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-store-line"></i>
                      </div>
                      WooCommerce
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-shopping-cart-line"></i>
                      </div>
                      Magento
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-amazon-fill"></i>
                      </div>
                      Amazon
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Communication Tools</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-slack-fill"></i>
                      </div>
                      Slack
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-microsoft-fill"></i>
                      </div>
                      Teams
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-video-line"></i>
                      </div>
                      Zoom
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center mr-2">
                        <i className="ri-mail-line"></i>
                      </div>
                      Email Systems
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <div className="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                  <i className="ri-line-chart-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-6">Integration Methods</h3>
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">API-First Approach</h4>
                  <p className="text-sm text-gray-600 mb-3">Modern RESTful APIs for scalable integrations</p>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: '95%' }}></div>
                  </div>
                  <span className="text-xs text-gray-500">95% Success Rate</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Webhook Integration</h4>
                  <p className="text-sm text-gray-600 mb-3">Real-time event-driven data synchronization</p>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: '92%' }}></div>
                  </div>
                  <span className="text-xs text-gray-500">92% Success Rate</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">ETL Processes</h4>
                  <p className="text-sm text-gray-600 mb-3">Extract, Transform, Load for data migration</p>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: '88%' }}></div>
                  </div>
                  <span className="text-xs text-gray-500">88% Success Rate</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Custom Connectors</h4>
                  <p className="text-sm text-gray-600 mb-3">Tailored solutions for unique requirements</p>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: '90%' }}></div>
                  </div>
                  <span className="text-xs text-gray-500">90% Success Rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Workflow Builder */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Visual Workflow Builder</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Design, test, and deploy intelligent automation workflows with our intuitive drag-and-drop interface. No coding required.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <h3 className="text-2xl font-bold mb-6">Workflow Components</h3>
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-primary">
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mr-3">
                      <div className="w-4 h-4 flex items-center justify-center text-white">
                        <i className="ri-play-line"></i>
                      </div>
                    </div>
                    <h4 className="font-semibold text-gray-800">Triggers</h4>
                  </div>
                  <p className="text-sm text-gray-600">Start workflows based on events, schedules, or conditions</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-600">
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center mr-3">
                      <div className="w-4 h-4 flex items-center justify-center text-white">
                        <i className="ri-settings-line"></i>
                      </div>
                    </div>
                    <h4 className="font-semibold text-gray-800">Actions</h4>
                  </div>
                  <p className="text-sm text-gray-600">Execute tasks like data processing, API calls, or notifications</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 border-l-4 border-purple-600">
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center mr-3">
                      <div className="w-4 h-4 flex items-center justify-center text-white">
                        <i className="ri-git-branch-line"></i>
                      </div>
                    </div>
                    <h4 className="font-semibold text-gray-800">Conditions</h4>
                  </div>
                  <p className="text-sm text-gray-600">Add logic and decision points to control workflow flow</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 border-l-4 border-orange-600">
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center mr-3">
                      <div className="w-4 h-4 flex items-center justify-center text-white">
                        <i className="ri-loop-right-line"></i>
                      </div>
                    </div>
                    <h4 className="font-semibold text-gray-800">Loops</h4>
                  </div>
                  <p className="text-sm text-gray-600">Repeat actions for multiple items or until conditions are met</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <h3 className="text-2xl font-bold mb-6">Workflow Performance</h3>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-primary">2,847</div>
                  <div className="text-sm text-gray-600">Active Workflows</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">99.8%</div>
                  <div className="text-sm text-gray-600">Success Rate</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">4.2M</div>
                  <div className="text-sm text-gray-600">Tasks Automated</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">73%</div>
                  <div className="text-sm text-gray-600">Time Saved</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Technical Specifications</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Enterprise-grade automation and integration platform built for scalability, security, and reliability.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-primary">
                  <i className="ri-shield-check-line"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-4">Security Features</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• End-to-end encryption</li>
                <li>• OAuth 2.0 authentication</li>
                <li>• Role-based access control</li>
                <li>• Audit logging</li>
                <li>• SOC 2 Type II compliance</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-green-600">
                  <i className="ri-speed-line"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-4">Performance Metrics</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• 99.9% uptime SLA</li>
                <li>• &lt; 100ms API response time</li>
                <li>• 10,000+ concurrent workflows</li>
                <li>• Auto-scaling infrastructure</li>
                <li>• Global CDN deployment</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-purple-600">
                  <i className="ri-code-line"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-4">Supported Protocols</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• REST & GraphQL APIs</li>
                <li>• SOAP web services</li>
                <li>• Webhooks & WebSockets</li>
                <li>• FTP/SFTP file transfer</li>
                <li>• Database connectors</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-orange-600">
                  <i className="ri-expand-up-down-line"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-4">Scalability Options</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Horizontal scaling</li>
                <li>• Load balancing</li>
                <li>• Multi-region deployment</li>
                <li>• Containerized architecture</li>
                <li>• Kubernetes orchestration</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Partners */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Integration Partners</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Certified integrations with leading platforms and systems to ensure seamless connectivity across your technology stack.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
            {[
              { icon: 'ri-microsoft-fill', name: 'Microsoft', badge: 'Certified', color: 'text-blue-600' },
              { icon: 'ri-amazon-fill', name: 'AWS', badge: 'Partner', color: 'text-orange-500' },
              { icon: 'ri-google-fill', name: 'Google', badge: 'Certified', color: 'text-red-500' },
              { icon: 'ri-building-line', name: 'Salesforce', badge: 'AppExchange', color: 'text-blue-700' },
              { icon: 'ri-shopping-bag-line', name: 'Shopify', badge: 'Partner', color: 'text-green-600' },
              { icon: 'ri-slack-fill', name: 'Slack', badge: 'Verified', color: 'text-purple-600' },
              { icon: 'ri-database-2-line', name: 'Oracle', badge: 'Certified', color: 'text-indigo-600' },
              { icon: 'ri-pie-chart-line', name: 'SAP', badge: 'Partner', color: 'text-pink-600' },
              { icon: 'ri-mail-line', name: 'Mailchimp', badge: 'Certified', color: 'text-teal-600' },
              { icon: 'ri-paypal-fill', name: 'PayPal', badge: 'Partner', color: 'text-yellow-600' },
              { icon: 'ri-github-fill', name: 'GitHub', badge: 'Verified', color: 'text-gray-700' },
              { icon: 'ri-video-line', name: 'Zoom', badge: 'ISV Partner', color: 'text-blue-500' },
            ].map((partner, index) => (
              <div key={index} className="flex flex-col items-center p-6 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 flex items-center justify-center ${partner.color} mb-3`}>
                  <i className={`${partner.icon} text-3xl`}></i>
                </div>
                <span className="text-sm font-medium text-gray-700">{partner.name}</span>
                <span className="text-xs text-gray-500">{partner.badge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://thyamis.com/wp-content/uploads/2025/01/ai-automation-cta-bg.jpg)',
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Automate Your Business Processes Today</h2>
            <p className="text-gray-200 mb-8">
              Stop wasting time on manual tasks. Our AI automation and integration experts are ready to transform your operations, reduce costs, and accelerate your digital transformation journey.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={scrollToForm}
                className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium inline-flex items-center hover:bg-primary/90 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-calendar-line"></i>
                </div>
                Schedule Demo
              </button>
              <a
                href="tel:+31610749083"
                className="bg-transparent border border-white text-white px-6 py-3 rounded-button whitespace-nowrap font-medium inline-flex items-center hover:bg-white/10 transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-phone-line"></i>
                </div>
                Call: +31 (0)616300011
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Request Form */}
      <section className="py-16 bg-gray-50" id="demo-form">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Request a Demo</h2>
              <p className="text-gray-600">
                See how our AI automation and integration solutions can transform your business operations.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <form onSubmit={handleSubmit} data-readdy-form>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="+31 6 1234 5678"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Your Company"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Size *
                    </label>
                    <select
                      name="companySize"
                      value={formData.companySize}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select company size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="500+">500+ employees</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Services of Interest
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        'Robotic Process Automation',
                        'API Integration',
                        'Workflow Automation',
                        'Enterprise Connectivity',
                        'Cloud Integration',
                        'Legacy System Integration',
                      ].map((service) => (
                        <label key={service} className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.services.includes(service)}
                            onChange={() => handleCheckboxChange(service)}
                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                          />
                          <span className="ml-2 text-sm text-gray-700">{service}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Tell us about your automation needs..."
                    ></textarea>
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {formData.message.length}/500 characters
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.consent}
                        onChange={(e) => setFormData(prev => ({ ...prev, consent: e.target.checked }))}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary mt-1 cursor-pointer"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        I agree to the privacy policy and consent to being contacted about AI automation solutions.
                      </span>
                    </label>
                  </div>
                </div>
                <div className="mt-6">
                  <button
                    type="submit"
                    className="w-full bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer"
                  >
                    Request Demo
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSuccessModal(false)}></div>
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-green-500">
                  <i className="ri-check-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Demo Request Submitted!</h3>
              <p className="text-gray-600 mb-6">
                Thank you for your interest in our AI automation solutions. Our automation specialists will contact you within 24 hours to schedule your personalized demo.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="bg-primary text-white px-6 py-2 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
